/**
 * CafePod, 21 Sep 2026. Enhances the purchase-type cards (CP-030, CP-032, CP-033, CP-037).
 *
 * Liquid renders everything; this module only reflects the shopper's choice:
 *   - the Add to cart label carries the chosen price
 *   - the sticky bar states the purchase type and price
 *   - the reassurance strip switches its delivery threshold (via a data attribute
 *     on .product-details that CSS reads)
 *   - the cadence select is copied into the Subscribe radio's value, so the
 *     chosen plan submits with the form
 *
 * Variant changes: Horizon re-renders only its own components (price, variant
 * picker, buy buttons, sticky bar) from the section HTML it fetches, and hands
 * that HTML to listeners on the variant:update event. The CafePod blocks are not
 * among the components it morphs, so this module refreshes the purchase options,
 * the price anchor and the availability panel from that same HTML, then puts the
 * shopper's choice back. The choice is stored on the section element, not on this
 * component, so it survives the swap.
 *
 * Widget mode (data-mode="widget", the default since 21 Sep): the Recharge
 * subscription widget owns the radios. This module then reads the product form's
 * selling_plan value (FormData covers inputs inside the form and inputs bound to
 * it by the form attribute, which is how the widget attaches) whenever anything
 * in the section changes, and writes the same section state the cards mode
 * writes. Everything downstream is unchanged. The fallback poll (the widget sets
 * its hidden input by property, which no observer sees) runs only while the buy
 * box is on screen and the tab is visible (CP-092); the shopper cannot change the
 * plan while the widget is off screen, and the sticky bar just repeats the last
 * state.
 *
 * Loaded as type="module" with fetchpriority="low" from snippets/cp-purchase.liquid.
 */
import { ThemeEvents } from '@theme/events';

const EVENT_NAME = 'cp:purchase-change';
const REFRESH_BLOCKS = ['.cp-block--price', '.cp-block--availability'];

class CpPurchase extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document.body;
    this.details = this.closest('.product-details') || this.section;

    if (this.dataset.preselected === 'subscription' && !this.section.dataset.cpType) {
      const sub = this.querySelector('input[data-cp-type="subscription"]');
      this.section.dataset.cpType = 'subscription';
      this.section.dataset.cpPlan = sub?.value || '';
    }

    this.addEventListener('change', this.onChange);
    this.section.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    if (this.isWidgetMode) {
      this.section.addEventListener('change', this.syncFromForm, true);
      this.section.addEventListener('click', this.scheduleSync, true);
      // Child-list only: the widget re-rendering its inputs is the case worth catching.
      // Attribute churn from Okendo and Recharge used to trigger a resync on every mutation.
      this.observer = new MutationObserver(this.scheduleSync);
      this.observer.observe(this.section, { subtree: true, childList: true });
      // The widget renders in a shadow root and updates its hidden selling_plan input by
      // property, which no observer sees. A light poll is the reliable signal; it reads one
      // FormData and touches the DOM only when the value has changed. It runs only while
      // the buy box is in view and the tab is visible.
      this.inView = true;
      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            this.inView = entries.some((entry) => entry.isIntersecting);
            this.updatePolling();
          },
          { rootMargin: '300px 0px' }
        );
        this.viewObserver.observe(this.form || this);
      }
      document.addEventListener('visibilitychange', this.updatePolling);
      this.syncFromForm();
      this.updatePolling();
    }

    this.restore();
    this.apply(false);
  }

  get isWidgetMode() {
    return this.dataset.mode === 'widget';
  }

  get form() {
    return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
  }

  updatePolling = () => {
    const shouldPoll = this.inView !== false && document.visibilityState !== 'hidden';
    if (shouldPoll && !this.pollTimer) {
      this.pollTimer = setInterval(this.syncFromForm, 500);
      this.syncFromForm();
    } else if (!shouldPoll && this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  };

  scheduleSync = () => {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(this.syncFromForm, 60);
  };

  /** Widget mode: the form's selling_plan value is the truth. */
  syncFromForm = () => {
    const form = this.form;
    if (!form) return;
    let plan = '';
    try {
      plan = String(new FormData(form).get('selling_plan') || '');
    } catch (e) {
      plan = '';
    }
    const type = plan ? 'subscription' : 'onetime';
    if (this.section.dataset.cpType === type && (this.section.dataset.cpPlan || '') === plan) return;
    this.section.dataset.cpType = type;
    this.section.dataset.cpPlan = plan;
    this.apply(true);
  };

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    this.section?.removeEventListener('change', this.syncFromForm, true);
    this.section?.removeEventListener('click', this.scheduleSync, true);
    this.observer?.disconnect();
    this.viewObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.updatePolling);
    clearTimeout(this.syncTimer);
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  get type() {
    return this.section.dataset.cpType || '';
  }

  get plan() {
    return this.section.dataset.cpPlan || '';
  }

  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('input[data-cp-type]')) {
      const type = target.dataset.cpType || '';
      this.section.dataset.cpType = type;
      if (type === 'subscription') {
        const select = this.querySelector('[data-cp-cadence]');
        const plan = select?.value || target.value;
        this.section.dataset.cpPlan = plan;
        target.value = plan;
      }
      this.apply(true);
      return;
    }

    if (target.matches('[data-cp-cadence]')) {
      // Choosing a cadence is choosing to subscribe.
      const sub = this.querySelector('input[data-cp-type="subscription"]');
      if (!sub) return;
      sub.value = target.value;
      sub.checked = true;
      this.section.dataset.cpType = 'subscription';
      this.section.dataset.cpPlan = target.value;
      this.apply(true);
    }
  };

  onVariantUpdate = (event) => {
    const detail = event?.detail || {};
    const productId = detail.data?.productId;
    if (productId && this.section.querySelector('product-form-component')?.dataset.productId !== String(productId)) {
      // Not this product (a recommendations card in the same section, for instance).
    } else {
      this.refreshFrom(detail.data?.html);
    }
    const run = () => {
      this.restore();
      this.apply(false);
    };
    requestAnimationFrame(run);
    // Horizon's own components morph in the same tick; run again once they have settled.
    setTimeout(run, 120);
    setTimeout(run, 450);
  };

  /**
   * Pulls fresh markup for the CafePod blocks out of the section HTML Horizon fetched.
   * @param {Document | DocumentFragment | Element | undefined} html
   */
  refreshFrom(html) {
    if (!html || typeof html.querySelector !== 'function') return;

    const fresh = html.querySelector('cp-purchase');
    if (fresh) {
      for (const name of ['data-price', 'data-sub-price', 'data-pack-label']) {
        const value = fresh.getAttribute(name);
        if (value !== null) this.setAttribute(name, value);
      }
      this.innerHTML = fresh.innerHTML;
    }

    for (const selector of REFRESH_BLOCKS) {
      const next = html.querySelector(selector);
      const current = this.section.querySelector(selector);
      if (next && current) current.innerHTML = next.innerHTML;
    }
  }

  restore() {
    const type = this.type;
    const plan = this.plan;
    this.querySelectorAll('input[data-cp-type]').forEach((radio) => {
      radio.checked = radio.dataset.cpType === type;
      if (radio.dataset.cpType === 'subscription' && plan) radio.value = plan;
    });
    const select = this.querySelector('[data-cp-cadence]');
    if (select && plan) select.value = plan;
  }

  apply(announce) {
    const type = this.type;
    const isSub = type === 'subscription';
    const price = isSub ? this.dataset.subPrice : this.dataset.price;
    const pack = this.dataset.packLabel || '';
    const typeLabel = isSub ? this.dataset.subLabel : this.dataset.onetimeLabel;

    if (type) {
      this.details.dataset.cpPurchase = type;
    } else {
      delete this.details.dataset.cpPurchase;
    }

    this.querySelectorAll('[data-cp-opt]').forEach((card) => {
      card.classList.toggle('is-selected', card.dataset.cpOpt === type);
    });

    if (price) {
      this.section.querySelectorAll('[data-cp-atc-price]').forEach((el) => (el.textContent = price));
      this.section.querySelectorAll('[data-cp-sticky-price]').forEach((el) => (el.textContent = price));
    }
    this.section.querySelectorAll('[data-cp-sticky-type]').forEach((el) => {
      const parts = [];
      if (this.isWidgetMode || this.querySelector('input[data-cp-type]')) parts.push(typeLabel);
      if (pack) parts.push(pack);
      el.textContent = parts.join(' · ');
    });

    if (announce) {
      const select = this.querySelector('[data-cp-cadence]');
      const cadence = select?.selectedOptions?.[0]?.textContent?.trim() || '';
      document.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          bubbles: true,
          detail: { purchase_type: type, selling_plan_id: isSub ? this.plan : '', cadence: isSub ? cadence : '' },
        })
      );
    }
  }
}

if (!customElements.get('cp-purchase')) {
  customElements.define('cp-purchase', CpPurchase);
}
